import { IonButton, IonIcon, IonRouterLink, IonText } from '@ionic/react';
import React, { useMemo } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import Selection from '@/components/selection/Selection';
import { ScheduleFlowSummary } from '@/components/summary';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import { Dentist } from '@/data/Dentists';
import { useToast } from '@/hooks';
import { SCHEDULE_FLOW_STEPS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { isClinicName } from '@/utils/helpers/isClinicName';
import {
  arrowForwardCircleOutline,
  calendarOutline,
  informationCircleOutline
} from 'ionicons/icons';

import './SelectDentist.css';

const SelectDentist: React.FC = () => {
  const { t } = useLanguage();
  const displayToast = useToast();
  const {
    clinic,
    dentists,
    selectedDentist,
    setSelectedDentist,
    setCurrentStep,
    setAvailableDates,
    setSelectedDate,
    setSelectedTime,
    setAvailableTimeslots
  } = useScheduleFlow();

  if (!clinic) return null;

  const individualDentist = dentists.length === 1;
  const skipSelectDentist = individualDentist && selectedDentist;
  const isSelectedDentistUsingClinicName = isClinicName(
    `${selectedDentist?.first_name} ${selectedDentist?.last_name}`
  );

  const isAllDentistsUsingClinicName = dentists.every((dentist) =>
    isClinicName(`${dentist.first_name} ${dentist.last_name}`)
  );
  const isSomeDentistsUsingClinicName = dentists.some((dentist) =>
    isClinicName(`${dentist.first_name} ${dentist.last_name}`)
  );

  const copy = useMemo(() => {
    const selectionPromptKey = isAllDentistsUsingClinicName
      ? 'clinic'
      : isSomeDentistsUsingClinicName
        ? 'clinicOrDentist'
        : 'dentist';

    if (dentists.length === 0) {
      return {
        subHeader: t('schedule.selectDentist.subHeader'),
        header: clinic.name,
        message: t('schedule.selectDentist.messageWithoutAvailableDentists'),
        buttonText: t('schedule.selectDentist.buttonTextContactUs')
      };
    }

    return {
      subHeader: t('schedule.selectDentist.subHeader'),
      header: clinic.name,
      message: skipSelectDentist
        ? t('schedule.selectDentist.messageContinue')
        : t('schedule.selectDentist.messageWithSelection', {
            selectionPrompt: t(
              `schedule.selectDentist.selectionPrompts.${selectionPromptKey}`
            )
          }),
      buttonText: skipSelectDentist
        ? t('schedule.selectDentist.buttonTextContinue')
        : t('schedule.selectDentist.buttonTextNext')
    };
  }, [
    t,
    clinic.name,
    skipSelectDentist,
    isAllDentistsUsingClinicName,
    isSomeDentistsUsingClinicName
  ]);

  const getLabel = (dentist: Dentist) => {
    return (
      <>
        {dentist.specialization ? (
          <div className="dentist">
            <div className="name">
              {formatDentistName({
                dentist,
                returnDentistFullName: true
              })}
            </div>
            <div className="specialization">
              {dentist.specialization && `${dentist.specialization}`}
            </div>
          </div>
        ) : (
          <div className="dentist dentist--no-specialization">
            <div className="name">
              {formatDentistName({
                dentist,
                returnDentistFullName: true
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  const isSelected = (dentist: Dentist) => dentist.id === selectedDentist?.id;

  const selectDentist = (dentist: Dentist | null) => {
    setSelectedDentist(dentist);

    if (!dentist || dentist.id !== selectedDentist?.id) {
      setAvailableDates([]);
      setSelectedDate(null);
      setAvailableTimeslots([]);
      setSelectedTime(null);
    }
  };

  const onNext = () => {
    if (dentists.length === 0) {
      window.open(`tel:${clinic.mobile_number}`, '_blank');
      return;
    }

    if (selectedDentist) {
      setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
    } else {
      displayToast({
        message: t('schedule.selectDentist.errorMessage'),
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  };

  return (
    <div className="select-dentist">
      <IonIcon
        color="primary"
        icon={calendarOutline}
        className="select-dentist__icon"
      />
      <h4 className="select-dentist__sub-header">{copy.subHeader}</h4>
      <h1 className="select-dentist__header">{copy.header}</h1>
      <p className="select-dentist__message">{copy.message}</p>
      <Spacer top={24} bottom={102}>
        {dentists.length === 0 && (
          <div className="select-dentist__no-available-dentists">
            <IonIcon icon={informationCircleOutline} />
            <IonText>
              For inquiries, please contact us at{' '}
              <IonRouterLink
                color="primary"
                href={`tel:${clinic.mobile_number}`}
              >
                {formatMobileNumber(clinic.mobile_number)}
              </IonRouterLink>
            </IonText>
          </div>
        )}
        {skipSelectDentist ? (
          <ScheduleFlowSummary
            branchName={
              isSelectedDentistUsingClinicName
                ? formatDentistName({
                    dentist: selectedDentist,
                    returnClinicFullName: true
                  })
                : null
            }
            dentist={isSelectedDentistUsingClinicName ? null : selectedDentist}
            showEstimate={false}
          />
        ) : (
          <Selection
            options={dentists}
            getLabel={getLabel}
            onClick={selectDentist}
            isSelected={isSelected}
          />
        )}
      </Spacer>
      <ButtonContainer>
        <IonButton
          className="select-dentist__next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong
        >
          {dentists.length > 0 && (
            <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          )}
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default SelectDentist;
